// Dropped-connection recovery for long preflight calls.
//
// Safari aborts silent fetches around ~50s, and a preflight on a large logo
// (or a multi-cell bulk run) legitimately takes that long. The server keeps
// going and persists the result on teamStores/{id}.logos[].preflight — so on
// a network-level fetch failure the UI polls the store doc until the relevant
// entry's lastRun is NEWER than the newest lastRun seen before the run
// started (server-side timestamps only: immune to client clock skew).
// Mirrors the pattern shipped on ghostfit-dashboard's prize-tier preflight
// (pf-helpers block + pollPreflightResult).

export type PreflightEntryLike = {
  status?: string;
  details?: string;
  lastRun?: unknown;
  runBy?: string;
};

export type StoreLike = {
  logos?: Array<{
    id?: string;
    logoId?: string;
    preflight?: Record<string, PreflightEntryLike | undefined>;
  } | null> | null;
};

// Normalize a lastRun value to epoch ms. The console writes ISO strings;
// tolerate Firestore Timestamp JSON shapes from other writers.
export function atMs(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? 0 : t;
  }
  if (typeof v === 'object') {
    const o = v as { _seconds?: unknown; seconds?: unknown };
    if (typeof o._seconds === 'number') return o._seconds * 1000;
    if (typeof o.seconds === 'number') return Number(o.seconds) * 1000;
  }
  return 0;
}

export function logoById(store: StoreLike | null | undefined, logoId: string) {
  const logos = store?.logos || [];
  return logos.find((l) => l && (l.id === logoId || l.logoId === logoId)) || null;
}

export function cellLastRunMs(store: StoreLike | null | undefined, logoId: string, cellKey: string): number {
  const entry = logoById(store, logoId)?.preflight?.[cellKey];
  return entry ? atMs(entry.lastRun) : 0;
}

// Newest lastRun across every cell of the logo — the freshness baseline for a
// bulk run, whose results land in one write when ALL cells are done.
export function maxLastRunMs(store: StoreLike | null | undefined, logoId: string): number {
  const map = logoById(store, logoId)?.preflight || {};
  let max = 0;
  for (const k of Object.keys(map)) {
    const t = map[k] ? atMs(map[k]!.lastRun) : 0;
    if (t > max) max = t;
  }
  return max;
}

export function cellRecovered(
  store: StoreLike | null | undefined,
  logoId: string,
  cellKey: string,
  prevMs: number
): PreflightEntryLike | null {
  const entry = logoById(store, logoId)?.preflight?.[cellKey];
  return entry && atMs(entry.lastRun) > prevMs ? entry : null;
}

export function bulkRecovered(
  store: StoreLike | null | undefined,
  logoId: string,
  prevMaxMs: number
): { total: number; passed: number } | null {
  const logo = logoById(store, logoId);
  if (!logo || maxLastRunMs(store, logoId) <= prevMaxMs) return null;
  const map = logo.preflight || {};
  let total = 0;
  let passed = 0;
  for (const k of Object.keys(map)) {
    total++;
    if ((map[k]?.status || '').toLowerCase() === 'pass') passed++;
  }
  return { total, passed };
}

// One bulk preflight run with dropped-connection recovery, shared by the
// StoreEditorDrawer and CreateStore wizard cards. Reads the freshness
// baseline from the server BEFORE running, fires `run`, and on a
// network-level failure (isRealError(e) false — e.g. not an ApiError) polls
// until the bulk transaction lands. Throws the original error for real
// (server-responded) failures and a descriptive Error on recovery timeout.
export async function runBulkPreflightWithRecovery(opts: {
  logoId: string;
  run: () => Promise<{ cellCount?: number; passedCount?: number }>;
  fetchStore: () => Promise<StoreLike>;
  isRealError: (e: unknown) => boolean;
  onRecovering?: () => void;
  attempts?: number;
  sleep?: (ms: number) => Promise<void>;
}): Promise<{ total: number; passed: number; recovered: boolean }> {
  const baseline = await opts.fetchStore().catch(() => null);
  const prevMaxMs = baseline ? maxLastRunMs(baseline, opts.logoId) : 0;
  try {
    const res = await opts.run();
    return { total: res.cellCount ?? 0, passed: res.passedCount ?? 0, recovered: false };
  } catch (e) {
    if (opts.isRealError(e)) throw e;
    opts.onRecovering?.();
    const rec = await pollStoreUntil(
      opts.fetchStore,
      (s) => bulkRecovered(s, opts.logoId, prevMaxMs),
      { attempts: opts.attempts ?? 60, sleep: opts.sleep }
    );
    if (!rec) {
      throw new Error('Connection dropped; no saved results appeared within 5 minutes. Reopen this store to check again.');
    }
    return { ...rec, recovered: true };
  }
}

const defaultSleep = (ms: number) => new Promise<void>((rs) => setTimeout(rs, ms));

// Poll fetchStore() until isReady(store) returns a truthy value, attempts ×
// intervalMs at most (36 × 5s ≈ 3 min for one cell; 60 ≈ 5 min for bulk runs
// that legitimately preflight many cells back to back). Fetch errors are
// treated as transient.
export async function pollStoreUntil<T>(
  fetchStore: () => Promise<StoreLike>,
  isReady: (store: StoreLike) => T | null,
  opts?: { attempts?: number; intervalMs?: number; sleep?: (ms: number) => Promise<void> }
): Promise<T | null> {
  const attempts = opts?.attempts ?? 36;
  const intervalMs = opts?.intervalMs ?? 5000;
  const sleep = opts?.sleep ?? defaultSleep;
  for (let i = 0; i < attempts; i++) {
    await sleep(intervalMs);
    try {
      const hit = isReady(await fetchStore());
      if (hit) return hit;
    } catch {
      // transient — keep polling
    }
  }
  return null;
}
